// src/components/FeatureSection.tsx
import dashboard from '../../assets/images/features/Dashboard.jpg';
import handsOnLearning from '../../assets/images/features/100_Hands_On_Learning.jpg';
import dragonMasteryMedalsAll from '../../assets/images/features/Dragon_Mastery_Medals_All.png';
import everGrowingCourseCatalog from '../../assets/images/features/Ever_Growing_Course_Catalog.jpg';
import realTimeMyCodeDragonAI from '../../assets/images/features/Real_Time_My Code_Dragon_AI.jpg';
import typingCourseScreenShot from '../../assets/images/features/TypingCourseScreenShot.jpg';
import unlockYourChildsCodingPotential from '../../assets/images/features/Unlock_Your_Childs_Coding_Potential.jpg';
import Image from './Image';
import { Link } from 'react-router-dom';

import React from 'react';

const features = [
  {
    title: '100% Hands On Learning, No More Boring Videos',
    description: (
      <>
        ✓ Write real code in every session, instead of passively watching
        someone else. <br />
        ✓ This makes coding fun and prevents the boredom and disengagement that
        often comes with purely video based courses. <br />
        <br />
        <strong className="text-center block">
          Give Your Child a Head Start in Coding –{' '}
          <Link
            to="/signup"
            className="text-blue-600 font-bold hover:text-blue-800 underline"
          >
            Sign Up Today
          </Link>
        </strong>
      </>
    ),
    imageSrc: handsOnLearning,
    imageAlt: 'Hands-on coding learning illustration',
  },
  {
    title:
      "Less Frustration & More Learning Moments with My Code Dragon AI's Feedback & Support",
    description: (
      <>
        ✓ When kids are learning to code, getting stuck on an error can be
        frustrating. That's where My Code Dragon's AI-powered assistant comes
        in, it's like a personal coding tutor available 24/7. <br />✓ As your
        child writes code, our My Code Dragon AI is there to provide debugging
        help that's easy to understand for young coders.
      </>
    ),
    imageSrc: realTimeMyCodeDragonAI,
    imageAlt: 'AI support illustration',
  },
  {
    title: 'Ever-Growing Course Catalog',
    description: (
      <>
        ✓ Offering self-paced courses in Python, R, SQL, JavaScript, and HTML &
        CSS (with more on the way). <br />✓ Check out our{' '}
        <Link
          to="/courses"
          className="text-blue-600 font-bold hover:text-blue-800 underline"
        >
          Courses
        </Link>
        . In total, there are 1,300+ interactive coding activities. <br />
        ✓ Students get instant access to every new course as soon as it's
        released, no extra fees or upgrades required. <br />✓ Sign Up today to
        unlock our entire course library and all future additions at no
        additional cost!{' '}
        <Link
          to="/signup"
          className="text-blue-600 font-bold hover:text-blue-800 underline"
        >
          Sign Up
        </Link>
      </>
    ),
    imageSrc: everGrowingCourseCatalog,
    imageAlt: 'Course catalog illustration',
  },
  {
    title: 'Typing Foundations for Coding Success',
    description: (
      <>
        ✓ New typers often have trouble figuring out how to type parentheses,
        quotes, underscores, semicolons, etc. <br />
        ✓ My Code Dragon offers a dedicated Typing course to help new typers
        improve their typing speed and accuracy, especially with those tricky
        symbols used in coding. <br />
        ✓ With My Code Dragon AI feedback, the AI gently corrects them and
        offers tips (for example, instructing the student to hold the Shift
        button and hit the double quote button, which is located next to the
        Enter key at the same time). <br />✓ Over time, they gain confidence in
        both their typing and coding abilities, since they can focus on logic
        and creativity instead of hunting for keys.
      </>
    ),
    imageSrc: typingCourseScreenShot,
    imageAlt: 'Typing course illustration',
  },
  {
    title: 'Dragon Mastery Medals',
    description: (
      <>
        Our Dragon Mastery Medals reward students for their performance in each
        course. This three-tier system motivates kids to write accurate code and
        rely on their own skills before asking for AI help: <br />
        <br />✓ <strong>Gold Medal:</strong> Awarded for completing a course
        with high accuracy and mostly independent coding. <br />✓{' '}
        <strong>Silver Medal:</strong> Finished a course with a few more
        mistakes and AI assistance along the way. <br />✓{' '}
        <strong>Bronze Medal:</strong> Completed a course regardless of accuracy
        or AI help used. <br />
        <br />
        The highest medal earned is displayed on the student's course tiles,
        encouraging them to revisit lessons and strive for a higher medal next
        time. These medals inspire kids to keep improving. They'll aim to debug
        their code thoroughly and use the AI assistance more sparingly to reach
        Gold Dragon Mastery level accuracy and independence.
      </>
    ),
    imageSrc: dragonMasteryMedalsAll,
    imageAlt: 'Dragon Mastery Medals illustration',
  },
  {
    title: 'Progress Tracking & Personalized Dashboard',
    description: (
      <>
        Each student gets a personalized dashboard that visualizes their
        accomplishments and stats. It's easy for kids and parents to see
        progress at a glance, making it simple to stay motivated and on track
        with coding goals: <br />
        <br />✓ <strong>Set coding goals:</strong> Students can set personal
        goals, like coding for 60 minutes per week, and track their progress
        toward meeting them. <br />✓{' '}
        <strong>Count activities completed:</strong> Monitor the number of
        coding activities completed. <br />✓{' '}
        <strong>Courses completed by language:</strong> See how many courses
        were completed over time by coding language. <br />✓{' '}
        <strong>Dragon Mastery Medals Progress:</strong> Every course completed
        updates the medal tally. <br />✓{' '}
        <strong>Personalized performance insights:</strong> The dashboard
        highlights strengths and areas for improvement for each completed
        course.
      </>
    ),
    imageSrc: dashboard,
    imageAlt: 'Progress tracking dashboard illustration',
  },
  {
    title: "Unlock Your Child's Coding Potential - Enroll Now",
    description: (
      <>
        ✓ Spark a lifelong passion for technology and innovation. Don't miss
        this chance to unlock your child's coding potential and set them on the
        path to success.{' '}
        <Link
          to="/signup"
          className="text-blue-600 hover:text-blue-800 font-medium underline"
        >
          Get Started Today
        </Link>
      </>
    ),
    imageSrc: unlockYourChildsCodingPotential,
    imageAlt: 'Enroll now illustration',
  },
];

interface FeatureCardProps {
  title: string;
  description: string | React.ReactNode;
  imageSrc: string;
  imageAlt: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  title,
  description,
  imageSrc,
  imageAlt,
}) => (
  <div className="flex flex-col items-center text-center bg-white p-4 md:p-6 rounded-md shadow-md transition-transform transform hover:scale-105 hover:shadow-lg">
    <h3 className="text-[20px] md:text-[24px] font-medium mb-4 text-[#023f81] w-full">
      {title}
    </h3>
    <div className="flex items-center justify-center w-full mb-4">
      <Image
        src={imageSrc}
        alt={imageAlt}
        className="object-contain w-full max-w-[400px] md:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px]"
      />
    </div>
    <div className="w-full">
      <p className="text-[16px] md:text-[18px] text-gray-600 text-left md:text-justify whitespace-pre-line">
        {description}
      </p>
    </div>
  </div>
);

const FeatureSection: React.FC = () => (
  <div className="mx-2 md:mx-8 lg:mx-16 xl:mx-40 p-2 md:p-6">
    <div className="text-[1.2rem] md:text-[1.3rem] lg:text-[1.5rem] font-bold text-center flex justify-center mb-6 md:mb-8">
      Why My Code Dragon?
    </div>
    <div className="space-y-6 md:space-y-8">
      {features.map((feature, index) => (
        <FeatureCard
          key={index}
          title={feature.title}
          description={feature.description}
          imageSrc={feature.imageSrc}
          imageAlt={feature.imageAlt}
        />
      ))}
    </div>
  </div>
);

export default FeatureSection;
